/**
 * PostHog feature flag keys, shared by web-app and admin3 — both read the same
 * project, so one rollout gates the feature on both sides.
 *
 * Flags live per PostHog project, so every key added here must also be created
 * in each project (US-prod, EU-prod, non-prod).
 */
export enum FeatureFlag {
  /**
   * Kill switch for all web-app capture, enforced in its before_send hook.
   * Retiring it means deleting the flag and that check together. Inert in
   * admin3, which captures nothing.
   */
  PosthogCaptureEnabled = 'posthog-capture-enabled',

  /** Gates the aggregate queue metrics cards on the Queue Dashboard. */
  ReportingQueueMetrics = 'reporting-queue-metrics',

  /**
   * Gates the desktop Release Notifications opt-in in Update Settings. Served by
   * customer-service behind the API gateway (see desktopNotificationsApi).
   * CUS-590.
   */
  ReleaseNotifications = 'release-notifications-ui',

  /**
   * Gates the automatic orphaned-deskphone-call cleanup (watchdog force-remove
   * + WebSocket-reconnect sweep). Off by default so the removal side-effects can
   * be ramped from the PostHog dashboard and killed instantly if a live call is
   * ever wrongly reaped. The manual Dismiss button and WebSocket event-replay
   * ship unconditionally — they can't remove a live call on their own.
   */
  OrphanedCallCleanup = 'orphaned-call-cleanup',

  /**
   * Gates the step-by-step AI agent creation wizard against the legacy single
   * form in admin3. Off → legacy form (fallback); on → wizard.
   */
  AiAgentCreateWizard = 'ai-agent-create-wizard',

  /**
   * Gates the webphone single-call audio path that skips the AudioContext
   * (macOS stutter on output-device changes). Off → single calls use the
   * AudioContext graph (default). On → the remote stream plays directly on the
   * <audio> element. Conferences always use the graph. CUS-732.
   */
  WebphoneSingleCallAudioBypass = 'webphone-single-call-audio-bypass',

  /**
   * Loads the desktop call-pop window from its lean `popup.html` entry, which
   * leaves out main-window-only services (chat-rtm WebSocket, support chat,
   * updater, Sentry Replay). The main window sends the value to Electron in
   * `popup-settings`. When it changes, Electron replaces a hidden popup at
   * once, and a visible one when it is next hidden. Off → full `/popup`.
   */
  DesktopLeanPopup = 'desktop-lean-popup',

  /**
   * Its payload holds the remote announcements that web-app shows in the
   * banner area. Targeting controls who receives them. See
   * `docs/announcements.md` for the schema and the workflow.
   */
  Announcements = 'announcements',

  /**
   * Gates the Admin 2 → admin3 Calls list cutover (MAP-230). Admin 2 points
   * staff at the embedded admin3 Calls page; when the flag is off, that page
   * sends the top frame back to the native Admin 2 list, so a staff user who
   * is not in the rollout audience never sees admin3 Calls. Targeted by
   * `email` during the reviewer rollout, then 100%. The native Calls detail
   * page is not gated here: it stays in Admin 2 until the CDR project moves
   * it (MAP-266).
   */
  Admin3IframeCalls = 'admin3-iframe-calls',

  /**
   * Shows Export CSV on the admin3 Calls list (MAP-345). The file holds the
   * same masked values as the list (ADR-0028), built in the browser from the
   * BFF list route. Off until compliance accepts ADR-0032.
   */
  Admin3CallsExportCsv = 'admin3-calls-export-csv',

  /**
   * Gates the Admin 2 → admin3 Companies list cutover (MAP-278, MAP-230
   * pattern). Admin 2 points staff at the embedded admin3 Companies page;
   * when the flag is off, that page sends the top frame back to the native
   * Admin 2 list, so a staff user outside the rollout audience never sees
   * admin3 Companies. Targeted by `email` during the reviewer rollout, then
   * 100%.
   */
  Admin3IframeCompanies = 'admin3-iframe-companies',
}
